import { officerAccountStatus } from "./officerStatus";

// The figures on the verifier dashboard (pages/InstitutionDashboard.jsx).
// Every one is counted from what the server returned; nothing is assumed,
// and a figure the page could not read is null rather than a guess.

const HOUR_MS = 60 * 60 * 1000;
const HOURS_SHOWN_AS_HOURS = 48;

const countOf = (list) => (Array.isArray(list) ? list.length : 0);

/** A count as a whole percentage of a total; 0 when the total is 0. */
export function percentOf(count, total) {
  return total > 0 ? Math.round((count / total) * 100) : 0;
}

/**
 * Requests by status, from the server's list for each status (PENDING,
 * VERIFIED, REJECTED, WITHDRAWN). The total is their sum, so every figure
 * is one the officer can open as a list.
 */
export function requestStatusCounts(requests) {
  const pending = countOf(requests?.pending);
  const verified = countOf(requests?.verified);
  const rejected = countOf(requests?.rejected);
  const withdrawn = countOf(requests?.withdrawn);
  return {
    pending,
    verified,
    rejected,
    withdrawn,
    total: pending + verified + rejected + withdrawn,
  };
}

/**
 * Officer invitations not accepted yet, or null when the list was not read.
 * An organisation's are its pending invitations
 * (GET /organisations/:id/invites); an institution's invitees are officers
 * who have not set a password yet.
 */
export function openInvitationCount(kind, { invites, officers } = {}) {
  if (kind === "organisation") {
    return Array.isArray(invites) ? invites.length : null;
  }
  if (!Array.isArray(officers)) return null;
  return officers.filter(
    (officer) => officerAccountStatus(officer) === "PENDING",
  ).length;
}

/**
 * Join requests an organisation admin still has to answer, or null when the
 * list was not read (GET /organisations/:id/join-requests; answered ones
 * stay in the list as APPROVED or REJECTED).
 */
export function waitingJoinRequestCount(joinRequests) {
  if (!Array.isArray(joinRequests)) return null;
  return joinRequests.filter((request) => request?.status === "PENDING").length;
}

/**
 * What "Needs attention" counts: pending requests, plus invitations and
 * join requests when they are known.
 */
export function openItemCount({
  pendingRequests = 0,
  openInvitations = null,
  joinRequestsWaiting = null,
}) {
  return pendingRequests + (openInvitations ?? 0) + (joinRequestsWaiting ?? 0);
}

/**
 * Reads one "Needs attention" figure: `{ count, error: null }` when `read`
 * resolves with the count, `{ count: null, error }` when it fails, with the
 * server's message or `failure`. Never rejects, so one figure that cannot
 * be read does not take the rest of the dashboard with it.
 */
export async function readAttentionCount(read, failure) {
  try {
    return { count: await read(), error: null };
  } catch (err) {
    return { count: null, error: err?.message || failure };
  }
}

/** A figure has a card once it was read, or once reading it failed. */
export function attentionFigureShown(figure) {
  return figure?.count != null || figure?.error != null;
}

/** The number on a figure's card: the count, or "-" when it could not be read. */
export function attentionFigureNumber(figure) {
  return figure?.error ? "-" : (figure?.count ?? "-");
}

/**
 * Mean time from a request arriving to its decision, in milliseconds, over
 * the decided requests that carry both times; null when there are none.
 */
export function averageTurnaroundMs(decidedRequests) {
  const spans = (decidedRequests ?? [])
    .map(
      (request) =>
        Date.parse(request?.decidedAt) - Date.parse(request?.createdAt),
    )
    .filter((ms) => Number.isFinite(ms) && ms >= 0);
  if (spans.length === 0) return null;
  return spans.reduce((sum, ms) => sum + ms, 0) / spans.length;
}

/** "< 1 hr", "5 hrs", "3 days"; "None yet" before the first decision. */
export function formatTurnaround(ms) {
  if (ms === null || ms === undefined) return "None yet";
  const hours = ms / HOUR_MS;
  if (hours < 1) return "< 1 hr";
  if (hours < HOURS_SHOWN_AS_HOURS) {
    const wholeHours = Math.round(hours);
    return `${wholeHours} ${wholeHours === 1 ? "hr" : "hrs"}`;
  }
  return `${Math.round(hours / 24)} days`;
}
